/**
 * The drawer's own chrome: which tabs exist, and which of them a given detail level shows
 * (slate-feature-transfer W8 §B.1).
 *
 * Two deliberate departures from kms:
 *  - **the `prompt` tab is gone.** Its own copy said the builder had moved into Timeline; the
 *    tab rendered a hint and a "Go to Timeline" button. Six tabs, not seven.
 *  - **a tab with no panel is not rendered.** kms mounted every tab and let some of them show
 *    nothing. Here `tabsFor` intersects the density set with what the host actually registered,
 *    so a tab always leads somewhere.
 */

export const CONVERSATION_TABS = [
  "chat",
  "timeline",
  "context",
  "thinkers",
  "sources",
  "outputs",
] as const;
export type ConversationTab = (typeof CONVERSATION_TABS)[number];

export const CONVERSATION_DENSITIES = ["simple", "medium", "expert"] as const;
export type ConversationDensity = (typeof CONVERSATION_DENSITIES)[number];

export const DENSITY_TABS: Record<ConversationDensity, ConversationTab[]> = {
  simple: ["chat"],
  medium: ["chat", "timeline", "thinkers", "sources"],
  expert: ["chat", "timeline", "context", "thinkers", "sources", "outputs"],
};

/** Glyphs are decoration; the LABEL is the accessible name and is translated at the call site. */
export const TAB_GLYPH: Record<ConversationTab, string> = {
  chat: "💬",
  timeline: "🧵",
  context: "📎",
  thinkers: "🧠",
  sources: "🔎",
  outputs: "📤",
};

export const DENSITY_INITIAL: Record<ConversationDensity, string> = {
  simple: "S",
  medium: "M",
  expert: "E",
};

const DENSITY_KEY = "conversation.density";

/** localStorage can hold anything; an unreadable value is the default, never a crash. */
export function readDensity(): ConversationDensity {
  try {
    const stored = localStorage.getItem(DENSITY_KEY);
    return CONVERSATION_DENSITIES.find((entry) => entry === stored) ?? "expert";
  } catch {
    return "expert";
  }
}

export function writeDensity(density: ConversationDensity): void {
  try {
    localStorage.setItem(DENSITY_KEY, density);
  } catch {
    // A private window is allowed to forget the detail level.
  }
}

/** The density's tabs, minus the ones this build has no panel for. */
export function tabsFor(
  density: ConversationDensity,
  available: ReadonlySet<ConversationTab>,
): ConversationTab[] {
  return DENSITY_TABS[density].filter((tab) => available.has(tab));
}

/**
 * The snap rule: when the active tab is not in the current set — because the density changed,
 * or because a panel is not built yet — fall to the first tab that IS. Returns `null` only
 * when nothing at all is available, which the host renders as an empty state rather than a
 * blank strip.
 */
export function resolveTab(
  active: ConversationTab,
  density: ConversationDensity,
  available: ReadonlySet<ConversationTab>,
): ConversationTab | null {
  const tabs = tabsFor(density, available);
  if (tabs.includes(active)) {
    return active;
  }
  return tabs[0] ?? null;
}
